import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { colors } from "@/design-system/theme/colors";
import type { WidgetConfig } from "../../api";
import type { WidgetVersion } from "../../constants/widget-assets";
import { getWidgetAssetUrls } from "../../constants/widget-assets";
import {
  getOptimalFloatingBadgePosition,
  getOptimalNotificationBadgePosition,
} from "../../pages/customize/position-helpers";

interface WidgetPreviewProps {
  config: Partial<WidgetConfig>;
  deviceType: "desktop" | "tablet" | "mobile";
  isOpen: boolean;
  onToggle: () => void;
  widgetVersion?: WidgetVersion;
}

function WidgetPreview({
  config,
  deviceType,
  isOpen,
  onToggle,
  widgetVersion = "1",
}: WidgetPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isWidgetReady, setIsWidgetReady] = useState(false);

  const previewMargins = useMemo(() => {
    const scaleFactor =
      deviceType === "mobile" ? 0.35 : deviceType === "tablet" ? 0.45 : 0.55;
    const minMargin = 8;

    return {
      marginTop: Math.max(minMargin, Math.round((config.marginTop ?? 24) * scaleFactor)),
      marginBottom: Math.max(minMargin, Math.round((config.marginBottom ?? 24) * scaleFactor)),
      marginRight: Math.max(minMargin, Math.round((config.marginRight ?? 24) * scaleFactor)),
      marginLeft: Math.max(minMargin, Math.round((config.marginLeft ?? 24) * scaleFactor)),
    };
  }, [
    config.marginTop,
    config.marginBottom,
    config.marginRight,
    config.marginLeft,
    deviceType,
  ]);

  const floatingBadgePosition = useMemo(() => {
    const position = config.position || "bottom-right";
    const userPosition = config.floatingBadge?.position;
    if (userPosition && userPosition !== "left") {
      return userPosition;
    }
    return getOptimalFloatingBadgePosition(position);
  }, [config.position, config.floatingBadge?.position]);

  const widgetConfig = useMemo(
    () => ({
      widgetId: "",
      widgetType: "chat-preview",

      brandName: config.brandName || "Support",
      brandSubtitle: config.brandSubtitle || "",
      phoneNumber: config.phoneNumber || "",
      defaultMessage: config.defaultMessage || "Hello! I have a question.",
      ctaText: config.ctaText || "Start Chat",
      agentName: config.agentName || "Support",
      welcomeMessage: config.welcomeMessage || "Hi there! How can we help you today?",
      onlineStatusText: config.onlineStatusText || "Online now",
      offlineStatusText: config.offlineStatusText || "We will reply soon",

      primaryColor: config.primaryColor || "#25D366",
      secondaryColor: config.secondaryColor || "#128C7E",
      headerColor: config.headerColor || config.primaryColor || "#25D366",
      backgroundColor: config.backgroundColor || "#FFFFFF",
      textColor: config.textColor || "#1F2937",
      hoverColor: config.hoverColor || "#1EBE5A",
      hoverBackgroundColor: config.hoverBackgroundColor || "#1EBE5A",
      hoverIconColor: config.hoverIconColor || "#FFFFFF",
      badgeColor: config.badgeColor || "#FF4D4F",
      statusOnlineColor: config.statusOnlineColor || "#52C41A",
      statusOfflineColor: config.statusOfflineColor || "#8C8C8C",
      borderColor: config.borderColor || "#E5E7EB",

      useGradient: config.useGradient ?? false,
      gradientDirection: config.gradientDirection || "135deg",
      gradientType: config.gradientType || "linear",

      buttonShape: config.buttonShape || "circle",
      buttonStyle: config.buttonStyle || "solid",
      borderRadius: config.borderRadius ?? 16,
      shadowStyle: config.shadowStyle || "medium",
      borderStyle: config.borderStyle || "none",
      borderWidth: config.borderWidth ?? 0,
      iconSize: config.iconSize ?? 28,
      iconPadding: config.iconPadding ?? 14,

      fontFamily: config.fontFamily || '"Readex Pro", system-ui, sans-serif',
      fontWeight: config.fontWeight ?? 500,
      fontSize: config.fontSize ?? 14,
      lineHeight: config.lineHeight ?? 1.5,
      textCase: config.textCase || "none",

      iconType: config.iconType || "whatsapp",
      iconAnimation: config.iconAnimation || "none",

      closeIconType: config.closeIconType || "x",
      closeIconColor: config.closeIconColor || "#FFFFFF",
      closeIconBackgroundColor: config.closeIconBackgroundColor || "transparent",
      closeIconSize: config.closeIconSize ?? 20,

      position: config.position || "bottom-right",
      marginTop: previewMargins.marginTop,
      marginBottom: previewMargins.marginBottom,
      marginRight: previewMargins.marginRight,
      marginLeft: previewMargins.marginLeft,
      zIndex: 999999,

      animationType: config.animationType || "slide",
      animationDuration: config.animationDuration ?? 300,

      openByDefault: isOpen,
      showBadge: config.showBadge ?? true,
      badgeText: config.badgeText || "1",
      badgePosition: getOptimalNotificationBadgePosition(config.position || "bottom-right"),

      floatingBadge: {
        enabled: config.floatingBadge?.enabled ?? true,
        message: config.floatingBadge?.message || "How can we help you?",
        backgroundColor: config.floatingBadge?.backgroundColor || "#FFFFFF",
        textColor: config.floatingBadge?.textColor || "#1F2937",
        fontSize: config.floatingBadge?.fontSize || 14,
        glossyEffect: config.floatingBadge?.glossyEffect ?? true,
        borderColor: config.floatingBadge?.borderColor || "#E5E7EB",
        borderWidth: config.floatingBadge?.borderWidth ?? 1,
        position: floatingBadgePosition,
        maxWidth: config.floatingBadge?.maxWidth || 200,
        textAlign: config.floatingBadge?.textAlign || "left",
      },
    }),
    [config, floatingBadgePosition, isOpen, previewMargins]
  );

  const iframeSrcDoc = useMemo(() => {
    const configJson = JSON.stringify(widgetConfig);
    const urls = getWidgetAssetUrls(widgetVersion);

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body {
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: transparent;
    }
    .notifyy-widget {
      position: absolute !important;
    }
  </style>
</head>
<body>
  <script>
    window.NotifyyWidgetConfig = ${configJson};

    window.addEventListener('message', function(event) {
      if (event.data && event.data.type === 'updateConfig') {
        if (window.NotifyyWidget && window.NotifyyWidget.updateConfig) {
          window.NotifyyWidget.updateConfig(event.data.config);
        }
      } else if (event.data && event.data.type === 'toggle') {
        if (window.NotifyyWidget) window.NotifyyWidget.toggle();
      } else if (event.data && event.data.type === 'open') {
        if (window.NotifyyWidget) window.NotifyyWidget.open();
      } else if (event.data && event.data.type === 'close') {
        if (window.NotifyyWidget) window.NotifyyWidget.close();
      }
    });

    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '${urls.css}';
    document.head.appendChild(link);

    var script = document.createElement('script');
    script.src = '${urls.js}';
    script.onload = function() {
      // The widget script loads before it has necessarily created DOM nodes.
      // Poll briefly so we only mark "ready" once the widget root exists.
      var attempts = 0;
      var maxAttempts = 50; // ~5s
      var interval = setInterval(function() {
        attempts++;
        var hasWidgetRoot = !!document.querySelector('.notifyy-widget');
        if (window.NotifyyWidget && hasWidgetRoot) {
          clearInterval(interval);
          if (window.parent) window.parent.postMessage({ type: 'widgetReady' }, '*');
          return;
        }
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          if (window.parent) window.parent.postMessage({ type: 'widgetReady' }, '*');
        }
      }, 100);
    };
    document.body.appendChild(script);
  </script>
</body>
</html>`;
  }, [widgetConfig, widgetVersion]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "widgetReady") {
        setIsLoading(false);
        setIsWidgetReady(true);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    updateTimeoutRef.current = setTimeout(() => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: "updateConfig",
            config: widgetConfig,
          },
          "*"
        );
      }
    }, 100);

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [widgetConfig]);

  useEffect(() => {
    if (!isWidgetReady) return;

    const sendOpenCloseMessage = () => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: isOpen ? "open" : "close",
          },
          "*"
        );
      }
    };

    sendOpenCloseMessage();
    const retryTimeout = setTimeout(sendOpenCloseMessage, 200);
    return () => clearTimeout(retryTimeout);
  }, [isOpen, isWidgetReady]);

  const getDimensions = () => {
    switch (deviceType) {
      case "mobile":
        return { width: 280, height: 520 };
      case "tablet":
        return { width: 380, height: 460 };
      default:
        return { width: "100%", height: 440 };
    }
  };

  const dimensions = getDimensions();

  return (
    <div
      className="relative rounded-xl overflow-hidden mx-auto"
      style={{
        width: dimensions.width,
        height: dimensions.height,
        background: "linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)",
        border: deviceType !== "desktop" ? "5px solid #1f2937" : "none",
        borderRadius: deviceType !== "desktop" ? 20 : 12,
      }}
      onClick={onToggle}
    >
      {deviceType === "mobile" && (
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-4 rounded-b-xl z-10"
          style={{ backgroundColor: "#1f2937" }}
        />
      )}

      <div className="p-4 pt-6 pointer-events-none">
        <div className="h-2.5 rounded w-2/3 mb-2" style={{ backgroundColor: "#dee2e6" }} />
        <div className="h-2 rounded w-1/2 mb-3" style={{ backgroundColor: "#dee2e6" }} />
        <div className="h-12 rounded mb-2" style={{ backgroundColor: "#dee2e6" }} />
        <div className="h-2 rounded w-3/4 mb-1" style={{ backgroundColor: "#dee2e6" }} />
        <div className="h-2 rounded w-1/2" style={{ backgroundColor: "#dee2e6" }} />
      </div>

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50">
          <Loader2 size={24} className="animate-spin" style={{ color: colors.primary }} />
        </div>
      )}

      <iframe
        ref={iframeRef}
        title="Widget Preview"
        srcDoc={iframeSrcDoc}
        className="absolute inset-0 w-full h-full border-0"
        style={{
          background: "transparent",
          pointerEvents: "auto",
        }}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
}

export default WidgetPreview;
